import React, { useEffect } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { pathToRegexp } from 'path-to-regexp'
import cx from 'classnames'

import { withStyles, useTheme } from '@material-ui/core/styles'
import { Button, Grid, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'

import PositionButton from '~/components/Header/PositionButton'
import ImagePlaceholder from '~/components/ImagePlaceholder'
import SearchProductByStore from '~/components/Header/SearchProductByStore'
import ButtonBuyOnline from '~/components/ButtonBuyOnline'
import useEcommerce from '~/components/Ecommerce/useEcommerce'
import useViewportSize from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'

import 'simplebar/dist/simplebar.min.css'

import { mountUrl, replaceImagePath } from '~/utils'
import track from '~/track'

const styles = (theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'auto',
    ...theme.gofind.scrollBar,
    '&:has(.MuiDrawer-root)': {
      overflow: 'hidden'
    }
  },
  back: {
    gap: 8,
    display: 'flex',
    alignItems: 'center',
    paddingInline: 16,
    marginTop: 8
  },
  bar: {
    gap: 8,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  searchProduct: {
    padding: 16
  },
  content: {
    gap: 16,
    padding: '8px 24px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      maxWidth: '100%'
    }
  },
  footer: {
    gap: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.between(0, 330)]: {
      flexWrap: 'wrap',
      '& button': { width: '100%' }
    }
  },
  productImage: {
    transition: '.3s ease',
    backgroundColor: '#fff'
  },
  productImageLoading: {
    height: 300,
    filter: 'blur(5px)'
  },
  productName: {
    width: '100%',
    textAlign: 'justify'
  }
})

function ProductDetails({
  classes,
  data: { product },
  loading,
  store,
  onClose,
  onFind,
  position,
  config
}) {
  const {
    openEcommerce,
    setOpenEcommerce,
    dataEcommerce,
    loadingEcommerce,
    setApiEcommerce
  } = useEcommerce()
  const { getLiteral } = useLiterals()

  const history = useHistory()

  const urlRegex = pathToRegexp('/', null, { sensitive: true })
  const isPrevRouteStarted = urlRegex.test(history.location.state?.prevPath)

  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const image = replaceImagePath(product)

  useEffect(() => {
    if (history.location.state) {
      track('cliqueProdutoDetalhes', { category: 'produto', name: product?.name })
    } else {
      track('linkProdutoDetalhes', { name: product?.name })
    }
  }, [product])

  if (!product && config.showContextViewStore) return <Redirect to="/produto/todos" />
  if (!product && !config.showContextViewStore) return <Redirect to="/" />

  return (
    <Grid className={classes.root}>
      {!isMobile ? (
        <>
          <PositionButton classes={classes} position={position} />
          <Grid className={classes.searchProduct}>
            <SearchProductByStore
              isProductDetail
              product={product}
              position={position}
              config={config}
            />
          </Grid>
        </>
      ) : null}

      <Grid className={classes.bar}>
        <Grid className={classes.back}>
          <IconButton aria-label={getLiteral('1051')} onClick={onClose} size="small">
            <ArrowBack style={{ color: theme.palette.text.primary }} />
          </IconButton>
          {!isMobile ? <Typography variant="body1">{getLiteral('1051')}</Typography> : null}
        </Grid>

        <Grid className={classes.content}>
          {!isPrevRouteStarted ? (
            image ? (
              <img
                data-cy="product-image"
                src={mountUrl(image, { width: 300, height: 200 })}
                alt={product.name}
                className={cx(classes.productImage, {
                  [classes.productImageLoading]: loading
                })}
              />
            ) : (
              <Grid data-cy="product-without-image">{getLiteral('2030')}</Grid>
            )
          ) : image ? (
            <ImagePlaceholder
              src={mountUrl(image, { width: 300, height: 200 })}
              placeholder={mountUrl(image, { height: 100 })}
            >
              {(src, loading) => (
                <img
                  data-cy="product-image"
                  src={src}
                  alt={product.name}
                  className={cx(classes.productImage, {
                    [classes.productImageLoading]: loading
                  })}
                />
              )}
            </ImagePlaceholder>
          ) : (
            <Grid data-cy="product-without-image">{getLiteral('2030')}</Grid>
          )}

          <Typography
            title={product.name}
            data-cy="product-name"
            variant="h1"
            align="center"
            style={{ fontSize: 24, fontWeight: 'bold', marginTop: 8 }}
            gutterBottom
          >
            {product.name}
          </Typography>

          <Grid className={classes.footer}>
            <ButtonBuyOnline
              modal
              product={product}
              store={store}
              position={position}
              openEcommerce={openEcommerce}
              dataEcommerce={dataEcommerce}
              setOpenEcommerce={setOpenEcommerce}
              loadingEcommerce={loadingEcommerce}
              setApiEcommerce={setApiEcommerce}
            />

            <Button color="primary" variant="contained" onClick={() => onFind()}>
              {getLiteral('2598')}
            </Button>
          </Grid>

          <Typography
            title={product.description}
            data-cy="product-description"
            variant="body1"
            align="center"
            gutterBottom
          >
            {product.description}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default withStyles(styles)(ProductDetails)
